import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccountBalance as AccountIcon,
  TrendingUp,
  TrendingDown,
  ArrowForward,
} from '@mui/icons-material';
import { Account } from '../types/account';
import {
  formatCurrency,
  getBalanceColor,
  getAccountTypeLabel,
  getAccountTypeBadgeColor,
  getAccountStatusBadge,
} from '../utils/accountUtils';

interface AccountCardProps {
  account: Account;
  showPartnerInfo?: boolean;
  onClick?: () => void;
}

const AccountCard: React.FC<AccountCardProps> = ({
  account,
  showPartnerInfo = true,
  onClick,
}) => {
  const navigate = useNavigate();
  const statusBadge = getAccountStatusBadge(account);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/accounts/${account.accountNumber}`);
    }
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
      onClick={handleClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <AccountIcon color="primary" />
            <Typography variant="h6" component="div">
              {account.accountName}
            </Typography>
          </Box>
          <Tooltip title="View Details">
            <IconButton size="small" color="primary">
              <ArrowForward />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {account.accountNumber}
        </Typography>

        {showPartnerInfo && account.partnerName && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {account.partnerName}
          </Typography>
        )}

        <Box mt={2} mb={2}>
          <Typography variant="body2" color="text.secondary">
            Current Balance
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: getBalanceColor(account.currentBalance), fontWeight: 'bold' }}
          >
            {formatCurrency(account.currentBalance, account.currency)}
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Available Balance
          </Typography>
          <Typography variant="body1" sx={{ color: getBalanceColor(account.availableBalance) }}>
            {formatCurrency(account.availableBalance, account.currency)}
          </Typography>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip
            label={getAccountTypeLabel(account.accountType)}
            color={getAccountTypeBadgeColor(account.accountType)}
            size="small"
          />
          <Chip label={statusBadge.label} color={statusBadge.color} size="small" />
          <Chip label={account.currency} size="small" variant="outlined" />
          {account.canHaveNegativeBalance && (
            <Chip label="Credit Allowed" size="small" color="info" variant="outlined" />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AccountCard;
